import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ManagePlans = () => {
  const [plans, setPlans] = useState([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Website Services');
  const [price, setPrice] = useState('');
  const [features, setFeatures] = useState('');
  const [isRecommended, setIsRecommended] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(window.API_URL + '/plans');
      setPlans(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken');
    
    // Convert features string to array for preview, though backend handles string parsing too
    const planData = {
      title,
      category,
      price,
      features,
      isRecommended
    };

    try {
      if (editId) {
        await axios.put(`${window.API_URL}/plans/${editId}`, planData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        await axios.post(window.API_URL + '/plans', planData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      fetchPlans();
      resetForm();
    } catch (err) {
      console.error(err);
      alert(`Error ${editId ? 'updating' : 'adding'} plan`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setCategory('Website Services');
    setPrice('');
    setFeatures('');
    setIsRecommended(false);
    setEditId(null);
  };

  const handleEdit = (plan) => {
    setEditId(plan._id);
    setTitle(plan.title);
    setCategory(plan.category);
    setPrice(plan.price);
    setFeatures(plan.features.join(', '));
    setIsRecommended(plan.isRecommended);
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken');
    try {
      await axios.delete(`${window.API_URL}/plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPlans();
    } catch (err) {
      console.error(err);
      alert('Error deleting plan');
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8 text-gray-800">Manage Pricing Plans</h1>
      
      {/* Add/Edit Form */}
      <div className="bg-white p-6 rounded-xl shadow-md mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-[#f06600]">
            {editId ? 'Edit Plan' : 'Add New Plan'}
          </h2>
          {editId && (
            <button onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700 underline">
              Cancel Edit
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="text" placeholder="Plan Title (e.g., Pro Plan)" value={title} onChange={e => setTitle(e.target.value)} required className="border p-2 rounded" />
          
          <select value={category} onChange={e => setCategory(e.target.value)} required className="border p-2 rounded bg-white">
            <option value="Website Services">Website Services</option>
            <option value="Digital Marketing">Digital Marketing</option>
          </select>
          
          <input type="text" placeholder="Price (e.g., $99/mo)" value={price} onChange={e => setPrice(e.target.value)} required className="border p-2 rounded" />
          
          <div className="flex items-center space-x-2 border p-2 rounded bg-gray-50">
            <input type="checkbox" id="isRecommended" checked={isRecommended} onChange={e => setIsRecommended(e.target.checked)} className="w-5 h-5 text-[#f06600]" />
            <label htmlFor="isRecommended" className="font-medium text-gray-700">Mark as Recommended / Most Popular</label>
          </div>

          <textarea placeholder="Features (Comma separated, e.g., 5 Pages, SEO Optimized, Free Hosting)" value={features} onChange={e => setFeatures(e.target.value)} required className="border p-2 rounded md:col-span-2 h-24"></textarea>
          
          <button type="submit" disabled={isSubmitting} className={`md:col-span-2 text-white py-2 rounded-lg font-bold transition ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#f06600] hover:bg-[#d05800]'}`}>
            {isSubmitting ? (editId ? 'Updating...' : 'Adding...') : (editId ? 'Update Plan' : 'Add Plan')}
          </button>
        </form>
      </div>

      {/* Plans List */}
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Current Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map(plan => (
            <div key={plan._id} className={`border-2 rounded-xl p-6 flex flex-col ${plan.isRecommended ? 'border-[#f06600] relative' : 'border-gray-200'}`}>
              {plan.isRecommended && (
                <span className="absolute top-0 right-0 bg-[#f06600] text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                  Recommended
                </span>
              )}
              <div className="mb-4">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{plan.category}</span>
                <h3 className="font-bold text-2xl mt-1 text-gray-800">{plan.title}</h3>
                <p className="text-[#f06600] text-3xl font-extrabold mt-2">{plan.price}</p>
              </div>
              
              <ul className="mb-6 flex-1 space-y-2">
                {plan.features.map((feature, i) => (
                  <li key={i} className="text-gray-600 text-sm flex items-start">
                    <span className="text-green-500 mr-2">✓</span> {feature}
                  </li>
                ))}
              </ul>

              <div className="flex gap-2 mt-auto">
                <button onClick={() => handleEdit(plan)} className="w-1/2 py-2 bg-blue-100 text-blue-600 font-semibold rounded hover:bg-blue-200 transition">Edit</button>
                <button onClick={() => handleDelete(plan._id)} className="w-1/2 py-2 bg-red-100 text-red-600 font-semibold rounded hover:bg-red-200 transition">Delete</button>
              </div>
            </div>
          ))}
          {plans.length === 0 && <p className="text-gray-500 col-span-full">No pricing plans found.</p>}
        </div>
      </div>
    </div>
  );
};

export default ManagePlans;
